import { useJobillaOrgId } from '@/hooks'
import {
	exportTargetMappingIndexQueryKey,
	useExportIntegrationIndex,
	useExportTargetMappingCreate,
	useExportTargetMappingDelete,
	useExportTargetMappingIndex,
	useExportTargetMappingUpdate,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { ExportConfig, logger, useExportConfig } from '../../ui'

type UseNewExportConfigProps = {
	campaignId: string
}

export function useNewExportConfig({ campaignId }: UseNewExportConfigProps) {
	const jobillaOrgId = useJobillaOrgId()
	const queryClient = useQueryClient()

	// Returned from each mutation's onSuccess so mutateAsync awaits the refetch. This keeps
	// the export config in its "saving" state until the fresh mapping is cached, so the
	// controlled toggle never briefly re-reads stale query data after a save (the flicker).
	const invalidateMapping = () =>
		queryClient.invalidateQueries({
			queryKey: exportTargetMappingIndexQueryKey({
				campaign: campaignId,
				organisation: jobillaOrgId,
			}),
		})

	// Fetch export integration status
	const exportIntegration = useExportIntegrationIndex(
		{
			'filter[organisation_id]': jobillaOrgId,
		},
		{
			retry: (failureCount, error) => {
				if (error.status === 404 || error.status === 403) {
					return false
				}
				return failureCount >= 3
			},
		},
	)

	// Fetch current export target mapping
	const komboTargetMapping = useExportTargetMappingIndex(
		{
			campaign: campaignId,
			organisation: jobillaOrgId,
		},
		{
			retry: false,
			staleTime: Infinity,
		},
	)

	const currentMapping = komboTargetMapping.data?.data?.[0]
	const mappingId = currentMapping?.id

	const exportTargetMutation = useExportTargetMappingCreate({
		mutationKey: ['export-target-mapping', campaignId],
		onMutate: async () => {
			await queryClient.cancelQueries({
				queryKey: exportTargetMappingIndexQueryKey({
					campaign: campaignId,
					organisation: jobillaOrgId,
				}),
			})
		},
		onSuccess: invalidateMapping,
	})

	const updateMutation = useExportTargetMappingUpdate({
		mutationKey: ['export-target-mapping-update', campaignId],
		onSuccess: invalidateMapping,
	})

	const deleteMutation = useExportTargetMappingDelete({
		mutationKey: ['export-target-mapping-delete', campaignId],
		onSuccess: invalidateMapping,
	})

	// Prepare export config data
	const exportConfig: ExportConfig | undefined = komboTargetMapping.data
		? {
			exportTargetId: currentMapping?.externalId ?? null,
			syncDisqualifiedCandidates: currentMapping?.syncDisqualifiedCandidates ?? true,
			availableExportTargetSearch: '', // Not persisted, handled in widget
		}
		: undefined

	const handleExportConfigChange = async (config: ExportConfig) => {
		// No job selected → remove any existing mapping.
		if (!config.exportTargetId) {
			if (mappingId) { await deleteMutation.mutateAsync({ mapping: mappingId }) }
			return
		}

		// Same job, only the sync setting changed → patch it in place. Avoids the
		// delete+recreate churn (which re-runs the candidate backfill) and the flicker.
		if (mappingId && currentMapping?.externalId === config.exportTargetId) {
			await updateMutation.mutateAsync({
				mapping: mappingId,
				data: { syncDisqualifiedCandidates: config.syncDisqualifiedCandidates },
			})
			return
		}

		// The mapped job changed (or there was no mapping) → (re)create the mapping.
		const integrationId = exportIntegration.data?.[0]?.id
		if (!integrationId) {
			logger.warn('No export integration found for organisation')
			return
		}

		if (mappingId) { await deleteMutation.mutateAsync({ mapping: mappingId }) }

		await exportTargetMutation.mutateAsync({
			data: {
				campaignId,
				exportIntegrationId: integrationId,
				externalId: config.exportTargetId,
				syncDisqualifiedCandidates: config.syncDisqualifiedCandidates,
			},
		})
	}

	return useExportConfig({
		exportConfig,
		isPending: komboTargetMapping.isPending || exportIntegration.isPending,
		isSaving: exportTargetMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
		onExportConfigChange: handleExportConfigChange,
	})
}
